/**
 * facilityScopeParams.ts
 *
 * Query params that scope a Communication Center request to the facilities the
 * user has selected in the global facility picker.
 *
 *   no facility    -> {}                     (every facility)
 *   one facility   -> { facilityId: n }      (the param these requests always sent)
 *   several        -> { facilityIds: [...] } (axios sends facilityIds[]=a&facilityIds[]=b)
 *
 * The one-facility shape is kept on purpose so a single-facility user's request
 * does not change. The API reads `facilityIds[]` first and falls back to
 * `facilityId` (HasUnreadMessageCounts::requestedFacilityIds).
 */

export interface FacilityScopeParams {
  facilityId?: number;
  facilityIds?: number[];
}

/** Positive integer ids, de-duplicated, ascending. */
function cleanIds(ids: readonly number[]): number[] {
  const seen = new Set<number>();
  for (const raw of ids) {
    const n = Number(raw);
    if (Number.isInteger(n) && n > 0) seen.add(n);
  }
  return Array.from(seen).sort((a, b) => a - b);
}

export function facilityScopeParams(ids: readonly number[]): FacilityScopeParams {
  const clean = cleanIds(ids);
  if (clean.length === 0) return {};
  if (clean.length === 1) return { facilityId: clean[0] };
  return { facilityIds: clean };
}

/**
 * Stable query-key segment for a facility selection: the cleaned ids joined by
 * commas, '' for none. The same set in any order gives the same key.
 */
export function facilityScopeKey(ids: readonly number[]): string {
  return cleanIds(ids).join(',');
}
